import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, Switch,
  FormControlLabel, Divider, Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon, Refresh as RefreshIcon, Edit as EditIcon,
  Delete as DeleteIcon, Visibility as ViewIcon, CheckCircle, Cancel,
  Apple as AppleIcon, Android as AndroidIcon, CreditCard as StripeIcon,
  AttachMoney as MoneyIcon, People as PeopleIcon, Close as CloseIcon,
  TrendingUp as TrendingIcon, Warning as WarningIcon,
  MoreTime as ExtendIcon, Pause as SuspendIcon, PlayArrow as ReactivateIcon, Add as AddIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import PurchaseAttemptsPanel from './PurchaseAttemptsPanel';
import UpsellFunnelPanel from './UpsellFunnelPanel';
import EditorConfiguracionUsuario from '../../components/admin/EditorConfiguracionUsuario';

const PLAN_COLORS: Record<string, any> = {
  free: 'default',
  conductor: 'info',
  proteccion: 'success',
  empresarial: 'secondary',
};

const PAID_PLANS = ['conductor', 'proteccion', 'empresarial'];

const STATUS_COLORS: Record<string, any> = {
  active: 'success',
  trial: 'info',
  cancelled: 'warning',
  expired: 'error',
  suspended: 'error',
  billing_issue: 'error',
  free: 'default',
};

function formatDate(date: string | Date | null): string {
  if (!date) return '-';
  return new Date(date).toLocaleString('es-DO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getExpiryColor(date: string | Date | null): string {
  if (!date) return 'text.secondary';
  const days = Math.floor((new Date(date).getTime() - Date.now()) / 86400000);
  if (days < 0) return 'error.main';
  if (days < 7) return 'warning.main';
  return 'success.main';
}

function timeAgo(date: string | Date): string {
  if (!date) return '-';
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}

const PlatformIcon: React.FC<{ platform?: string }> = ({ platform }) => {
  if (platform === 'ios') return <Tooltip title="iOS"><AppleIcon fontSize="small" /></Tooltip>;
  if (platform === 'android') return <Tooltip title="Android"><AndroidIcon fontSize="small" /></Tooltip>;
  if (platform === 'stripe') return <Tooltip title="Stripe"><StripeIcon fontSize="small" /></Tooltip>;
  return <Typography variant="body2" color="text.secondary">-</Typography>;
};

function cleanFormForSubmit(form: any, isPaidPlan: boolean) {
  const data: any = {};
  if (form.plan !== undefined) data.plan = form.plan;
  if (form.status !== undefined) data.status = form.status;
  if (form.email !== undefined) data.email = form.email;
  if (form.platform !== undefined) data.platform = form.platform;
  if (form.hasUsedTrial !== undefined) data.hasUsedTrial = form.hasUsedTrial;

  if (isPaidPlan) {
    data.autoRenew = form.autoRenew || false;
    if (form.expiresAt) data.expiresAt = form.expiresAt;
    else data.expiresAt = '';
    if (form.currentPeriodStart) data.currentPeriodStart = form.currentPeriodStart;
    if (form.currentPeriodEnd) data.currentPeriodEnd = form.currentPeriodEnd;
  } else {
    data.autoRenew = false;
    data.expiresAt = '';
    data.currentPeriodStart = '';
    data.currentPeriodEnd = '';
  }

  return data;
}

interface ClientOption {
  userId: string;
  name: string;
  email: string;
  code: string;
  hasSubscription: boolean;
}

// Por qué terminó una suscripción. Mismos códigos que manda RevenueCat en `cancel_reason` y
// `expiration_reason`; el estado del portal no los guarda, solo el último evento.
const MOTIVOS_FIN: Record<string, string> = {
  BILLING_ERROR: 'Cobro rechazado (tarjeta sin fondos, vencida o sin método de pago)',
  UNSUBSCRIBE: 'El usuario canceló la renovación',
  CUSTOMER_SUPPORT: 'Reembolso o baja por soporte de la tienda',
  PRICE_INCREASE: 'No aceptó el aumento de precio',
  DEVELOPER_INITIATED: 'Cancelada por nosotros',
  SUBSCRIPTION_PAUSED: 'Pausada por el usuario (Android)',
  UNKNOWN: 'Motivo desconocido',
};

const fechaCorta = (v?: string | number | null) =>
  v ? new Date(v).toLocaleString('es-DO', { timeZone: 'America/Santo_Domingo', dateStyle: 'short', timeStyle: 'short' }) : '—';

// Qué fue cada entrada de la auditoría del plan, en palabras.
const ACCIONES_PLAN: Record<string, string> = {
  admin_create: 'Creada a mano desde el admin',
  admin_update: 'Editada desde el admin',
  admin_extend: 'Extendida desde el admin',
  admin_suspend: 'Suspendida desde el admin',
  admin_reactivate: 'Reactivada desde el admin',
  admin_config_update: 'Configuración cambiada desde el admin',
  subscription_expiration_error: 'Error al dar de baja',
  subscription_expired: 'Baja: se apagó en Traccar lo que no incluye el plan gratis',
  subscription_restored: 'Volvió a pagar: se le devolvió en Traccar lo que la baja quitó',
  coherencia_incoherente: 'Fechas incoherentes corregidas por el sistema',
};

const diasHasta = (v?: string | null) => (v ? Math.ceil((new Date(v).getTime() - Date.now()) / 86400000) : null);

// Diagnóstico de una suscripción, en el orden en que se pregunta: ¿está activa o no y por qué?,
// ¿de dónde viene? (tienda o admin), ¿qué se le quitó en Traccar?, ¿qué le ha pasado al plan?
// Cada parte dice lo que ES, no solo "nada": una sección vacía sin explicación confunde más
// que no tenerla (pasó el 19-sep-2026 con una suscripción creada desde el admin).
const DiagnosticoSuscripcion: React.FC<{ sub?: any }> = ({ sub }) => {
  const d = sub?.diagnostico;
  if (!d) return null;
  const esTienda = sub.paymentProvider === 'revenuecat';
  const activa = ['active', 'trial'].includes(sub.status) && sub.expiresAt && new Date(sub.expiresAt) > new Date();
  const dias = diasHasta(sub.expiresAt);
  const rc = d.revenueCat;
  const bajas = (d.acciones || []).filter((a: any) => a.equiposLimpiados?.length || a.avisosQuitados?.length);
  const otras = (d.acciones || []).filter((a: any) => !(a.equiposLimpiados?.length || a.avisosQuitados?.length));

  const vence = sub.expiresAt
    ? new Date(sub.expiresAt).toLocaleString('es-DO', { timeZone: 'America/Santo_Domingo', day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>Diagnóstico</Typography>

      <Typography variant="caption" color="text.secondary">Estado</Typography>
      {activa ? (
        <Alert severity="success" sx={{ mb: 2, mt: 0.5 }}>
          <strong>Activa</strong> ({sub.plan}{sub.status === 'trial' ? ', en prueba' : ''}) hasta el <strong>{vence}</strong>
          {dias !== null ? ` — faltan ${dias} día${dias === 1 ? '' : 's'}` : ''}.
        </Alert>
      ) : d.motivoFin ? (
        <Alert severity={d.motivoFin.codigo === 'BILLING_ERROR' ? 'error' : 'warning'} sx={{ mb: 2, mt: 0.5 }}>
          <strong>Terminó: {d.motivoFin.texto}.</strong> Aviso {d.motivoFin.evento} de la tienda, {fechaCorta(d.motivoFin.fecha)} ({d.motivoFin.codigo}).
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2, mt: 0.5 }}>
          <strong>No está activa</strong> ({sub.plan} / {sub.status}){vence ? `; vencía el ${vence}` : ''}.
          {esTienda ? ' La tienda no mandó motivo.' : ' La creó o cambió alguien desde el admin; no hay tienda que dé motivo.'}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">Origen</Typography>
      {!esTienda ? (
        <Typography variant="body2" sx={{ mb: 2 }}>
          Creada <strong>a mano desde el admin</strong>. No pasa por Apple ni Google: no hay cobros, ni webhooks, ni RevenueCat que consultar.
        </Typography>
      ) : rc?.error ? (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>Tienda ({sub.platform || '—'}), pero RevenueCat no respondió: {rc.error}</Typography>
      ) : (
        <Box sx={{ mb: 2, mt: 0.5 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Comprada en la tienda ({sub.platform || '—'}) vía RevenueCat. Consulta en vivo; última vez que abrió la app: <strong>{fechaCorta(rc?.ultimaVezEnLaApp)}</strong>
          </Typography>
          {(rc?.productos || []).map((p: any) => (
            <Paper key={p.producto} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {p.producto} · {p.tipoPeriodo}{p.sandbox ? ' · SANDBOX' : ''} · {p.tienda}
              </Typography>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                {[
                  ['Compra', fechaCorta(p.compra)],
                  ['Vence', fechaCorta(p.vence)],
                  ['Cobro fallido', p.cobroFallidoDetectado ? fechaCorta(p.cobroFallidoDetectado) : 'no'],
                  ['Canceló', p.cancelacionDetectada ? fechaCorta(p.cancelacionDetectada) : 'no'],
                  ['Gracia hasta', p.graciaHasta ? fechaCorta(p.graciaHasta) : 'sin gracia'],
                  ['Reembolso', p.reembolsada ? fechaCorta(p.reembolsada) : 'no'],
                ].map(([k, v]) => (
                  <Grid size={{ xs: 6, md: 4 }} key={k}>
                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                    <Typography variant="body2" color={k === 'Cobro fallido' && v !== 'no' ? 'error.main' : 'text.primary'}>{v}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ))}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">Lo que una baja le quitó en Traccar</Typography>
      {bajas.length === 0 ? (
        <Typography variant="body2" sx={{ mb: 2 }}>Nada: nunca se le ha dado de baja.</Typography>
      ) : (
        <Box sx={{ mt: 0.5, mb: 2 }}>
          {bajas.map((a: any, i: number) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {fechaCorta(a.fecha)}{a.motivo ? ` · ${a.motivo}` : ''} · por {a.por}
              </Typography>
              {a.equiposLimpiados.map((e: any) => (
                <Typography key={e.id} variant="body2">
                  • {e.nombre} (Traccar {e.id}){e.limiteQuitado ? ` — límite de velocidad quitado: ${e.limiteQuitado}` : ''}
                </Typography>
              ))}
              {a.avisosQuitados.length > 0 && (
                <Typography variant="body2">• Avisos quitados: {a.avisosQuitados.join(' · ')}</Typography>
              )}
            </Paper>
          ))}
          <Typography variant="caption" color="text.secondary">
            La baja no borra la configuración guardada: apaga en Traccar lo que el plan ya no incluye. Los valores de arriba sirven para reponerlo.
          </Typography>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">Historial del plan</Typography>
      {otras.length === 0 ? (
        <Typography variant="body2">Sin cambios registrados.</Typography>
      ) : (
        <Box sx={{ mt: 0.5 }}>
          {otras.map((a: any, i: number) => {
            const r = a.accion === 'subscription_restored' ? a.detalles || {} : null;
            return (
              <Box key={i} sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  • {fechaCorta(a.fecha)} — {ACCIONES_PLAN[a.accion] || a.accion} (por {a.por})
                </Typography>
                {r && (
                  <Box sx={{ pl: 2 }}>
                    {(r.equiposRepuestos || []).map((e: any) => (
                      <Typography key={e.id} variant="body2" color="success.main">
                        ✓ {e.nombre}{e.limiteRepuesto ? ` — límite repuesto: ${e.limiteRepuesto}` : ''}
                      </Typography>
                    ))}
                    {(r.avisosRepuestos || []).map((x: any) => (
                      <Typography key={x.notificationId} variant="body2" color="success.main">✓ Aviso repuesto: {x.type}</Typography>
                    ))}
                    {(r.alarmasRepuestas || []).map((x: any) => (
                      <Typography key={x.notificationId} variant="body2" color="success.main">✓ Alarmas repuestas: {x.repuestas.join(', ')}</Typography>
                    ))}
                    {(r.omitidos || []).map((o: any, j: number) => (
                      <Typography key={j} variant="body2" color="text.secondary">– No se repuso {o.que}: {o.porque}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const SubscriptionsPage: React.FC = () => {
  const [subs, setSubs] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [search, setSearch] = useState('');

  const [editDialog, setEditDialog] = useState<any>(null);
  const [detailDialog, setDetailDialog] = useState<any>(null);
  const [deleteDialog, setDeleteDialog] = useState<any>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [saving, setSaving] = useState(false);

  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState<any>({
    userId: '', email: '', plan: 'free', status: 'active',
    expiresAt: '', platform: '', autoRenew: false,
  });

  const [clientSearch, setClientSearch] = useState('');
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  const [extendDialog, setExtendDialog] = useState<any>(null);
  const [extendDays, setExtendDays] = useState(30);
  const [userConfig, setUserConfig] = useState<any>(null);

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (statusFilter) params.status = statusFilter;
      if (planFilter) params.plan = planFilter;
      if (search) params.search = search;
      const res = await apiService.getAdminSubscriptions(params);
      setSubs(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, planFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiService.getSubscriptionStats();
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleRefresh = () => { fetchSubs(); fetchStats(); };

  const searchClients = useCallback(async (q: string) => {
    if (q.length < 2) { setClientOptions([]); return; }
    setClientLoading(true);
    try {
      const res = await apiService.searchClientsForSubscription(q);
      setClientOptions(res.data || []);
    } catch {
      setClientOptions([]);
    } finally {
      setClientLoading(false);
    }
  }, []);

  const handleClientSearchChange = (_: any, value: string) => {
    setClientSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchClients(value), 300);
  };

  const handleClientSelect = (_: any, client: ClientOption | null) => {
    setSelectedClient(client);
    if (client) {
      setCreateForm((prev: any) => ({
        ...prev,
        userId: client.userId,
        email: client.email,
      }));
    }
  };

  const openCreate = () => {
    setCreateForm({ userId: '', email: '', plan: 'free', status: 'active', expiresAt: '', platform: '', autoRenew: false });
    setSelectedClient(null);
    setClientOptions([]);
    setClientSearch('');
    setCreateDialog(true);
  };

  const handleCreatePlanChange = (plan: string) => {
    const isPaid = PAID_PLANS.includes(plan);
    setCreateForm((prev: any) => ({
      ...prev,
      plan,
      status: isPaid ? 'active' : 'active',
      autoRenew: isPaid ? prev.autoRenew : false,
      expiresAt: isPaid ? prev.expiresAt : '',
    }));
  };

  const openEdit = (sub: any) => {
    setEditForm({
      plan: sub.plan || 'free',
      status: sub.status || 'free',
      expiresAt: sub.expiresAt ? new Date(sub.expiresAt).toISOString().slice(0, 16) : '',
      autoRenew: sub.autoRenew || false,
      email: sub.email || '',
      platform: sub.platform || '',
      currentPeriodStart: sub.currentPeriodStart ? new Date(sub.currentPeriodStart).toISOString().slice(0, 16) : '',
      currentPeriodEnd: sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toISOString().slice(0, 16) : '',
      hasUsedTrial: sub.hasUsedTrial || false,
    });
    setEditDialog(sub);
  };

  const handleEditPlanChange = (plan: string) => {
    const isPaid = PAID_PLANS.includes(plan);
    setEditForm((prev: any) => ({
      ...prev,
      plan,
      status: !isPaid ? 'active' : prev.status,
      autoRenew: isPaid ? prev.autoRenew : false,
      expiresAt: isPaid ? prev.expiresAt : '',
      currentPeriodStart: isPaid ? prev.currentPeriodStart : '',
      currentPeriodEnd: isPaid ? prev.currentPeriodEnd : '',
    }));
  };

  const handleSave = async () => {
    if (!editDialog) return;
    setSaving(true);
    try {
      const isPaid = PAID_PLANS.includes(editForm.plan);
      const data = cleanFormForSubmit(editForm, isPaid);
      await apiService.updateSubscription(editDialog.userId, data);
      setEditDialog(null);
      fetchSubs();
      fetchStats();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setSaving(true);
    try {
      await apiService.deleteSubscription(deleteDialog.userId);
      setDeleteDialog(null);
      fetchSubs();
      fetchStats();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (userId: string) => {
    try {
      const [subRes, configRes] = await Promise.all([
        apiService.getAdminSubscription(userId),
        apiService.getSubscriptionConfig(userId).catch(() => ({ data: null })),
      ]);
      setDetailDialog(subRes.data);
      setUserConfig(configRes?.data ?? null);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleCreate = async () => {
    if (!createForm.userId) return;
    setSaving(true);
    try {
      const isPaid = PAID_PLANS.includes(createForm.plan);
      const data = cleanFormForSubmit(createForm, isPaid);
      data.userId = createForm.userId;
      await apiService.createSubscription(data);
      setCreateDialog(false);
      fetchSubs();
      fetchStats();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleExtend = async () => {
    if (!extendDialog) return;
    setSaving(true);
    try {
      await apiService.extendSubscription(extendDialog.userId, extendDays);
      setExtendDialog(null);
      setExtendDays(30);
      fetchSubs();
      fetchStats();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async (userId: string) => {
    try {
      await apiService.suspendSubscription(userId);
      fetchSubs();
      fetchStats();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleReactivate = async (userId: string) => {
    try {
      await apiService.reactivateSubscription(userId);
      fetchSubs();
      fetchStats();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const isPaidCreate = PAID_PLANS.includes(createForm.plan);
  const isPaidEdit = PAID_PLANS.includes(editForm.plan);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Suscripciones</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Nueva Suscripcion
          </Button>
          <Tooltip title="Actualizar">
            <IconButton onClick={handleRefresh} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Activas', value: stats.active || 0, color: 'success.main', icon: <PeopleIcon /> },
            { label: 'Trials', value: stats.trials || 0, color: 'info.main', icon: <TrendingIcon /> },
            { label: 'Canceladas / Expiradas', value: (stats.cancelled || 0) + (stats.expired || 0), color: 'warning.main', icon: <WarningIcon /> },
            { label: 'MRR Estimado', value: `$${stats.estimatedMRR || 0}`, color: 'primary.main', icon: <MoneyIcon /> },
          ].map((s, i) => (
            <Grid size={{ xs: 6, md: 3 }} key={i}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ backgroundColor: s.color, color: 'white', borderRadius: 2, p: 1.5, display: 'flex' }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="h5" fontWeight={600}>{s.value}</Typography>
                    <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <PurchaseAttemptsPanel />

      <UpsellFunnelPanel />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select value={statusFilter} label="Estado" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                {Object.keys(STATUS_COLORS).map(s => (
                  <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Plan</InputLabel>
              <Select value={planFilter} label="Plan" onChange={(e) => { setPlanFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                {Object.keys(PLAN_COLORS).map(p => (
                  <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 6 }}>
            <TextField fullWidth size="small" placeholder="Buscar por userId o email..."
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {!loading && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Plataforma</TableCell>
                  <TableCell>Expira</TableCell>
                  <TableCell align="center">Auto Renew</TableCell>
                  <TableCell>Ultimo Evento</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subs.map((sub) => (
                  <TableRow key={sub._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} fontFamily="monospace">{sub.userId}</Typography>
                      {sub.email && <Typography variant="caption" color="text.secondary">{sub.email}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={sub.plan || 'free'} color={PLAN_COLORS[sub.plan] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={sub.status} color={STATUS_COLORS[sub.status] || 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="center"><PlatformIcon platform={sub.platform} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: getExpiryColor(sub.expiresAt), fontSize: '0.85rem' }}>
                        {sub.expiresAt ? formatDate(sub.expiresAt) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {sub.autoRenew ? <CheckCircle fontSize="small" color="success" /> : <Cancel fontSize="small" color="disabled" />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.8rem" fontFamily="monospace">{sub.lastRevenueCatEvent || '-'}</Typography>
                      {sub.lastRevenueCatEventDate && (
                        <Typography variant="caption" color="text.secondary">{timeAgo(sub.lastRevenueCatEventDate)}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => openDetail(sub.userId)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(sub)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      {PAID_PLANS.includes(sub.plan) && (
                        <Tooltip title="Extender">
                          <IconButton size="small" color="primary" onClick={() => setExtendDialog(sub)}>
                            <ExtendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {sub.status !== 'suspended' ? (
                        <Tooltip title="Suspender">
                          <IconButton size="small" color="warning" onClick={() => handleSuspend(sub.userId)}>
                            <SuspendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Reactivar">
                          <IconButton size="small" color="success" onClick={() => handleReactivate(sub.userId)}>
                            <ReactivateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => setDeleteDialog(sub)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {subs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No se encontraron suscripciones</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={total} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Filas por pagina"
            />
          </>
        )}
      </TableContainer>

      {/* Create Subscription Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Suscripcion</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(opt) => `${opt.name} (${opt.code}) - ${opt.email}`}
              value={selectedClient}
              onChange={handleClientSelect}
              onInputChange={handleClientSearchChange}
              inputValue={clientSearch}
              loading={clientLoading}
              noOptionsText={clientSearch.length < 2 ? 'Escribe 2+ caracteres para buscar...' : 'No se encontraron clientes'}
              renderOption={(props, opt) => (
                <li {...props} key={opt.userId}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={600}>{opt.name}</Typography>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">ID: {opt.userId}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">{opt.email} - {opt.code}</Typography>
                    {opt.hasSubscription && (
                      <Chip size="small" label="Ya tiene suscripcion" color="warning" sx={{ ml: 1 }} />
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Buscar cliente" placeholder="Nombre, email, codigo o user ID..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {clientLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {createForm.userId && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="User ID" value={createForm.userId} size="small"
                  InputProps={{ readOnly: !!selectedClient }} disabled={!!selectedClient}
                  onChange={(e) => setCreateForm({ ...createForm, userId: e.target.value })} />
                <TextField fullWidth label="Email" value={createForm.email} size="small"
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
              </Box>
            )}

            {!createForm.userId && !selectedClient && (
              <Box>
                <Divider sx={{ my: 0.5 }}><Typography variant="caption" color="text.secondary">o ingresa manualmente</Typography></Divider>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField fullWidth label="User ID" required size="small"
                    value={createForm.userId}
                    onChange={(e) => setCreateForm({ ...createForm, userId: e.target.value })} />
                  <TextField fullWidth label="Email" size="small"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
                </Box>
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select value={createForm.plan} label="Plan" onChange={(e) => handleCreatePlanChange(e.target.value)}>
                {Object.keys(PLAN_COLORS).map(p => (
                  <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {isPaidCreate && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select value={createForm.status} label="Estado" onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}>
                    {Object.keys(STATUS_COLORS).map(s => (
                      <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Duracion:</Typography>
                    {[{ label: '7d', days: 7 }, { label: '30d', days: 30 }, { label: '90d', days: 90 }, { label: '1 ano', days: 365 }].map(opt => (
                      <Button key={opt.days} size="small" variant="outlined" onClick={() => {
                        const d = new Date(); d.setDate(d.getDate() + opt.days);
                        setCreateForm({ ...createForm, expiresAt: d.toISOString().slice(0, 16) });
                      }}>{opt.label}</Button>
                    ))}
                  </Box>
                  <TextField fullWidth type="datetime-local" label="Fecha de expiracion"
                    value={createForm.expiresAt}
                    onChange={(e) => setCreateForm({ ...createForm, expiresAt: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </Box>

                <FormControl fullWidth>
                  <InputLabel>Plataforma</InputLabel>
                  <Select value={createForm.platform} label="Plataforma"
                    onChange={(e) => setCreateForm({ ...createForm, platform: e.target.value })}>
                    <MenuItem value="">Sin especificar</MenuItem>
                    <MenuItem value="ios">iOS</MenuItem>
                    <MenuItem value="android">Android</MenuItem>
                    <MenuItem value="stripe">Stripe</MenuItem>
                    <MenuItem value="web">Web</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={<Switch checked={createForm.autoRenew} onChange={(e) => setCreateForm({ ...createForm, autoRenew: e.target.checked })} />}
                  label="Auto Renovacion" />
              </>
            )}

            {!isPaidCreate && (
              <Alert severity="info" variant="outlined">
                El plan Free no requiere fecha de expiracion ni renovacion. El usuario tendra acceso a las funciones basicas.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving || !createForm.userId}>
            {saving ? <CircularProgress size={20} /> : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Editar Suscripcion - User {editDialog?.userId}
          {editDialog?.email && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{editDialog.email}</Typography>}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth label="Email" type="email"
              value={editForm.email || ''}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />

            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select value={editForm.plan || ''} label="Plan" onChange={(e) => handleEditPlanChange(e.target.value)}>
                {Object.keys(PLAN_COLORS).map(p => (
                  <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {isPaidEdit ? (
              <>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select value={editForm.status || ''} label="Estado" onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                    {Object.keys(STATUS_COLORS).map(s => (
                      <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Plataforma</InputLabel>
                  <Select value={editForm.platform || ''} label="Plataforma" onChange={(e) => setEditForm({ ...editForm, platform: e.target.value })}>
                    <MenuItem value="">Sin especificar</MenuItem>
                    <MenuItem value="ios">iOS</MenuItem>
                    <MenuItem value="android">Android</MenuItem>
                    <MenuItem value="stripe">Stripe</MenuItem>
                    <MenuItem value="web">Web</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Rapido:</Typography>
                  {[{ label: '7d', days: 7 }, { label: '30d', days: 30 }, { label: '90d', days: 90 }, { label: '1 ano', days: 365 }].map(opt => (
                    <Button key={opt.days} size="small" variant="outlined" onClick={() => {
                      const d = new Date(); d.setDate(d.getDate() + opt.days);
                      // Las dos fechas juntas. Quien decide si el cliente es premium es
                      // "Fin del periodo actual"; dejarla con el valor viejo daba un plan
                      // que en la tabla se veia vigente y en la practica estaba muerto.
                      const f = d.toISOString().slice(0, 16);
                      setEditForm({ ...editForm, expiresAt: f, currentPeriodEnd: f });
                    }}>{opt.label}</Button>
                  ))}
                </Box>

                <TextField fullWidth type="datetime-local" label="Fecha de expiracion"
                  value={editForm.expiresAt || ''}
                  onChange={(e) => setEditForm({ ...editForm, expiresAt: e.target.value, currentPeriodEnd: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
                <TextField fullWidth type="datetime-local" label="Inicio del periodo actual"
                  value={editForm.currentPeriodStart || ''} onChange={(e) => setEditForm({ ...editForm, currentPeriodStart: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
                <TextField fullWidth type="datetime-local" label="Fin del periodo actual"
                  value={editForm.currentPeriodEnd || ''} onChange={(e) => setEditForm({ ...editForm, currentPeriodEnd: e.target.value })}
                  InputLabelProps={{ shrink: true }} />

                <FormControlLabel
                  control={<Switch checked={editForm.autoRenew || false} onChange={(e) => setEditForm({ ...editForm, autoRenew: e.target.checked })} />}
                  label="Auto Renovacion" />
                <FormControlLabel
                  control={<Switch checked={editForm.hasUsedTrial || false} onChange={(e) => setEditForm({ ...editForm, hasUsedTrial: e.target.checked })} />}
                  label="Ha usado trial" />
              </>
            ) : (
              <Alert severity="info" variant="outlined">
                Al cambiar a Free se limpiaran las fechas de expiracion y el estado sera "active". El usuario mantendra acceso a funciones basicas.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Dialog */}
      <Dialog open={!!extendDialog} onClose={() => setExtendDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Extender Suscripcion - User {extendDialog?.userId}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {extendDialog && (
              <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                Expira: {extendDialog.expiresAt ? formatDate(extendDialog.expiresAt) : 'Sin fecha'}
                {extendDialog.status === 'expired' && ' (expirado - se reactivara)'}
              </Alert>
            )}
            <TextField fullWidth type="number" label="Dias a extender"
              value={extendDays} onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
              inputProps={{ min: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[7, 30, 60, 90, 180, 365].map(d => (
                <Button key={d} size="small" variant={extendDays === d ? 'contained' : 'outlined'}
                  onClick={() => setExtendDays(d)}>{d} dias</Button>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialog(null)}>Cancelar</Button>
          <Button onClick={handleExtend} variant="contained" disabled={saving || extendDays < 1}>
            {saving ? <CircularProgress size={20} /> : 'Extender'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Confirmar Eliminacion</DialogTitle>
        <DialogContent>
          <Typography>
            Eliminar la suscripcion del usuario <strong>{deleteDialog?.userId}</strong>
            {deleteDialog?.email && <> ({deleteDialog.email})</>}?
            Esta accion no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Suscripcion - User {detailDialog?.userId}
          <IconButton onClick={() => setDetailDialog(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailDialog && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Plan', value: detailDialog.plan },
                  { label: 'Estado', value: detailDialog.status },
                  { label: 'Plataforma', value: detailDialog.platform || '-' },
                  { label: 'Producto', value: detailDialog.productId || '-' },
                  { label: 'Expira', value: formatDate(detailDialog.expiresAt) },
                  { label: 'Auto Renovacion', value: detailDialog.autoRenew ? 'Si' : 'No' },
                  { label: 'Proveedor', value: detailDialog.paymentProvider || '-' },
                  { label: 'RC Customer ID', value: detailDialog.revenueCatCustomerId || '-' },
                  { label: 'Ha usado trial', value: detailDialog.hasUsedTrial ? 'Si' : 'No' },
                  { label: 'Creado', value: formatDate(detailDialog.createdAt) },
                  { label: 'Actualizado', value: formatDate(detailDialog.updatedAt) },
                  { label: 'Ultimo Evento RC', value: `${detailDialog.lastRevenueCatEvent || '-'} (${formatDate(detailDialog.lastRevenueCatEventDate)})` },
                ].map((item, i) => (
                  <Grid size={{ xs: 6, md: 4 }} key={i}>
                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" fontWeight={500}>{item.value}</Typography>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>Configuracion de Features</Typography>
              <EditorConfiguracionUsuario
                key={detailDialog.userId}
                userId={String(detailDialog.userId)}
                configuracion={userConfig}
                puedeEditar
                siempreEditando
                onGuardado={setUserConfig}
              />

              <Divider sx={{ my: 2 }} />
              <DiagnosticoSuscripcion sub={detailDialog} />

              {detailDialog.paymentProvider === 'revenuecat' && (<>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Historial de Webhooks ({detailDialog.webhookHistory?.length || 0})</Typography>

              {detailDialog.webhookHistory && detailDialog.webhookHistory.length > 0 ? (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {[...detailDialog.webhookHistory].reverse().map((wh: any, i: number) => {
                    const evt = wh.rawPayload?.event || wh.rawPayload;
                    return (
                      <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Chip size="small" label={wh.eventType}
                            color={['EXPIRATION', 'BILLING_ISSUE'].includes(wh.eventType) ? 'error' : wh.eventType === 'CANCELLATION' ? 'warning' : 'success'} />
                          <Typography variant="caption" color="text.secondary">{formatDate(wh.timestamp)}</Typography>
                        </Box>
                        {evt && (
                          <>
                            {(evt.cancel_reason || evt.expiration_reason) && (
                              <Typography variant="body2" fontWeight={600} color={(evt.cancel_reason || evt.expiration_reason) === 'BILLING_ERROR' ? 'error.main' : 'text.primary'} sx={{ mb: 0.5 }}>
                                Motivo: {MOTIVOS_FIN[evt.cancel_reason || evt.expiration_reason] || evt.cancel_reason || evt.expiration_reason}
                              </Typography>
                            )}
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" color="text.secondary">
                              {evt.period_type ? `periodo: ${evt.period_type} | ` : ''}
                              {evt.price != null ? `precio: ${evt.price} ${evt.currency || ''} | ` : ''}
                              {evt.expiration_at_ms ? `vence: ${formatDate(new Date(evt.expiration_at_ms).toISOString())} | ` : ''}
                              {evt.environment ? `${evt.environment} | ` : ''}
                              product: {evt.product_id || '-'} | tx: {evt.transaction_id || '-'} | store: {evt.store || '-'}
                            </Typography>
                          </>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <Typography color="text.secondary" variant="body2">Sin historial de webhooks</Typography>
              )}
              </>)}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionsPage;
